import { useState } from "react";
import { Link } from "react-router";
import { twJoin } from "tailwind-merge";

import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { cardSurfaceVariants } from "@/design-system/Card";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { Tabs } from "@/design-system/Tabs";
import { Tiles } from "@/design-system/Tiles";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

import { AvailableIntegrationsContent } from "../available-integrations/AvailableIntegrations";
import { usePortfolioAiProjects } from "./usePortfolioIntegrationProjects";

type IntegrationsTab = "manage" | "available";

/** Shows the management tab when the user is a super administrator or can manage AI settings in any project. */
export function PortfolioIntegrationsPage(): React.ReactNode {
  const { t } = useTranslation();
  const sessionUser = useSessionUser();
  const aiProjects = usePortfolioAiProjects();
  const canManageIntegrations = sessionUser.isSuperAdmin || aiProjects.rows.length > 0;
  const [requestedTab, setRequestedTab] = useState<IntegrationsTab>();
  const activeTab = requestedTab ?? (canManageIntegrations ? "manage" : "available");

  if (aiProjects.error) {
    return <ErrorPage error={aiProjects.error} />;
  }
  if (aiProjects.isLoading) {
    return <FullSizeLoader withPadding />;
  }

  return (
    <DocumentPage
      containerBoundary="inherit"
      tabTitle={t("page.available-integrations.title")}
      breadcrumbs={
        <Breadcrumbs pages={[{ name: t("page.portfolio.title") }, { name: t("page.available-integrations.title") }]} />
      }
    >
      <Tabs.Root
        activeTab={activeTab}
        tabs={[
          {
            value: "manage",
            label: t("page.portfolio-integrations.tabs.manage"),
            isHidden: !canManageIntegrations,
          },
          { value: "available", label: t("page.portfolio-integrations.tabs.available") },
        ]}
        onSelectTab={setRequestedTab}
      >
        <Tabs.Content value="manage">
          <ManageIntegrations showSuperAdminIntegrations={sessionUser.isSuperAdmin} />
        </Tabs.Content>
        <Tabs.Content value="available">
          <AvailableIntegrationsContent />
        </Tabs.Content>
      </Tabs.Root>
    </DocumentPage>
  );
}

function ManageIntegrations({ showSuperAdminIntegrations }: { showSuperAdminIntegrations: boolean }): React.ReactNode {
  const { t } = useTranslation();

  return (
    <Tiles columns={{ base: 1, container: { sm: 2, lg: 3 } }} gap="sm">
      <ManageIntegrationCard
        name={t("page.portfolio-integrations.ai.title")}
        description={t("page.portfolio-integrations.manage.ai-description")}
        to={routes.portfolio.manageAiIntegration()}
      />
      {showSuperAdminIntegrations && (
        <ManageIntegrationCard
          name={t("page.integration.rems.title")}
          description={t("page.portfolio-integrations.manage.rems-description")}
          to={routes.portfolio.manageRemsIntegration()}
        />
      )}
      {showSuperAdminIntegrations && (
        <ManageIntegrationCard
          name={t("page.integration.iloq.title")}
          description={t("page.portfolio-integrations.manage.iloq-description")}
          to={routes.portfolio.manageILoqIntegration()}
        />
      )}
    </Tiles>
  );
}

function ManageIntegrationCard({ name, description, to }: { name: string; description: string; to: string }) {
  return (
    <Link
      to={to}
      className={twJoin(
        "flex flex-col gap-space-sm p-space-md text-text-primary no-underline",
        cardSurfaceVariants({ interactive: true }),
      )}
    >
      <span className="text-body-emphasis">{name}</span>
      <span className="text-caption text-text-secondary">{description}</span>
    </Link>
  );
}
